import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as AiPrefsIcon } from "../../assets/icons/aiPrefs.svg";
import {
  DEFAULT_UI_LANGUAGE,
  toAiLanguage,
  toUiLanguage,
} from "../../i18n/aiLanguages";
import {
  peekAiPreferences,
  updateAiPreferences,
} from "../../services/aiService";
import { showOperationFailedNotification } from "../../services/notificationService";
import LanguageDropdown from "../common/LanguageDropdown";
import ToggleSwitch from "../common/ToggleSwitch";

const prefsToUiState = (prefs) => ({
  aiLanguage: toUiLanguage(prefs?.ai_language) || DEFAULT_UI_LANGUAGE,
  citationEnabled: prefs?.citation_enabled !== false,
  contextInclusionEnabled: prefs?.context_inclusion_enabled !== false,
});

const ReadingAssistantMenu = ({ isOpen, onClose, anchorRef }) => {
  const { t } = useTranslation();
  const menuRef = useRef(null);
  const [prefsUi, setPrefsUi] = useState(() =>
    prefsToUiState(peekAiPreferences()),
  );
  const [isSavingLanguage, setIsSavingLanguage] = useState(false);

  const { aiLanguage, citationEnabled, contextInclusionEnabled } = prefsUi;

  // Prefs were already loaded when the sidebar became ready — use cache only.
  useEffect(() => {
    if (!isOpen) return undefined;
    const cached = peekAiPreferences();
    if (cached) setPrefsUi(prefsToUiState(cached));
    return undefined;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (menuRef.current?.contains(target)) return;
      if (anchorRef?.current?.contains(target)) return;
      if (target?.closest?.(".language-dropdown-panel")) return;
      onClose();
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose, anchorRef]);

  const handleLanguageChange = async (uiCode) => {
    if (isSavingLanguage || uiCode === aiLanguage) return;

    const previous = aiLanguage;
    setPrefsUi((prev) => ({ ...prev, aiLanguage: uiCode }));
    setIsSavingLanguage(true);

    try {
      await updateAiPreferences({ ai_language: toAiLanguage(uiCode) });
    } catch {
      setPrefsUi((prev) => ({ ...prev, aiLanguage: previous }));
      showOperationFailedNotification(
        t("notifications.operations.saveAiPreferences"),
      );
    } finally {
      setIsSavingLanguage(false);
    }
  };

  const handleCitationToggle = async (checked) => {
    if (checked === citationEnabled) return;
    const previous = citationEnabled;
    setPrefsUi((prev) => ({ ...prev, citationEnabled: checked }));
    try {
      await updateAiPreferences({ citation_enabled: checked });
    } catch {
      setPrefsUi((prev) => ({ ...prev, citationEnabled: previous }));
      showOperationFailedNotification(
        t("notifications.operations.saveAiPreferences"),
      );
    }
  };

  const handleContextInclusionToggle = async (checked) => {
    if (checked === contextInclusionEnabled) return;
    const previous = contextInclusionEnabled;
    setPrefsUi((prev) => ({ ...prev, contextInclusionEnabled: checked }));
    try {
      await updateAiPreferences({ context_inclusion_enabled: checked });
    } catch {
      setPrefsUi((prev) => ({ ...prev, contextInclusionEnabled: previous }));
      showOperationFailedNotification(
        t("notifications.operations.saveAiPreferences"),
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className="reading-assistant-menu"
      role="menu"
      aria-label={t("toolbar.moreOptions")}
    >
      <div className="reading-assistant-menu-header" role="presentation">
        <AiPrefsIcon
          className="reading-assistant-menu-icon"
          aria-hidden="true"
        />
        <span>{t("readingAssistant.menu.preferences")}</span>
      </div>

      <div className="reading-assistant-menu-prefs">
        <div className="reading-assistant-menu-pref-row reading-assistant-menu-pref-row--language">
          <span>{t("readingAssistant.menu.prefs.aiLanguage")}</span>
          <LanguageDropdown
            value={aiLanguage}
            onChange={handleLanguageChange}
            ariaLabel={t("readingAssistant.menu.prefs.aiLanguage")}
            className="reading-assistant-menu-language"
            align="end"
          />
        </div>

        <div className="reading-assistant-menu-pref-row">
          <span>{t("readingAssistant.menu.prefs.citationEnabled")}</span>
          <ToggleSwitch
            checked={citationEnabled}
            onChange={handleCitationToggle}
            ariaLabel={t("readingAssistant.menu.prefs.citationEnabled")}
          />
        </div>

        <div className="reading-assistant-menu-pref-row">
          <span>
            {t("readingAssistant.menu.prefs.contextInclusionEnabled")}
          </span>
          <ToggleSwitch
            checked={contextInclusionEnabled}
            onChange={handleContextInclusionToggle}
            ariaLabel={t(
              "readingAssistant.menu.prefs.contextInclusionEnabled",
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default ReadingAssistantMenu;
